// T-373 — Editor di un piatto del menù (crea/modifica/elimina) con foto.

import {
  IonButton,
  IonButtons,
  IonChip,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonTitle,
  IonToggle,
  IonToolbar,
} from '@ionic/react';
import { useEffect, useMemo, useState } from 'react';
import {
  ALLERGEN_LABELS_IT,
  parseTakeawayVariant,
  parseMenuPrice,
  AllergenSchema,
  DIETARY_LABELS_IT,
  DietaryFlagSchema,
  type Allergen,
  type DietaryFlag,
  type MenuCategory,
  type MenuItem,
  type MenuItemInput,
} from '@trackeat/shared';
import { uploadItemPhoto, removeItemPhoto } from './menuApi.js';
import type { MenuType } from './menuType.js';
import { PhotoPicker } from './PhotoPicker.js';
import { toUserMessage } from '../../../lib/errors.js';
import { confirmDialog } from '../../../lib/ui/dialogs.js';

interface Props {
  menuType?: MenuType;
  isOpen: boolean;
  item: MenuItem | null; // null = creazione
  categories: MenuCategory[];
  defaultCategoryId: string | null;
  slug: string | null;
  paperColor: string;
  addItem: (input: MenuItemInput) => Promise<MenuItem>;
  editItem: (item: MenuItem, patch: Partial<MenuItem>) => Promise<MenuItem>;
  removeItem: (item: MenuItem, slug: string | null) => Promise<void>;
  onDone: () => void;
  onClose: () => void;
  setToast: (msg: string) => void;
}

function centsToEuro(c: number | null): string {
  if (c == null) return '';
  return (c / 100).toFixed(2).replace('.', ',');
}
function euroToCents(s: string): number | null {
  if (!s.trim()) return null;
  try { return parseMenuPrice(s); } catch { return NaN; }
}

export function DishEditorModal(props: Props): JSX.Element {
  const { isOpen, item, categories, defaultCategoryId, slug, paperColor } = props;
  const isEdit = !!item;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [longDesc, setLongDesc] = useState('');
  const [price, setPrice] = useState('');
  const [variantKind, setVariantKind] = useState<'single' | 'family' | 'piadina'>('single');
  const [variantPrice, setVariantPrice] = useState('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [allergens, setAllergens] = useState<Allergen[]>([]);
  const [dietary, setDietary] = useState<DietaryFlag[]>([]);
  const [visible, setVisible] = useState(true);
  const [outOfStock, setOutOfStock] = useState(false);
  const [saving, setSaving] = useState(false);

  // Foto in staging: blob da caricare, o richiesta di rimozione della foto esistente.
  const [photoBlob, setPhotoBlob] = useState<Blob | null>(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(item?.name ?? '');
    setDescription(item?.description ?? '');
    setLongDesc((item?.translations?.it?.long_description as string) ?? '');
    setPrice(centsToEuro(item?.price_cents ?? null));
    const variant = parseTakeawayVariant(item?.price_unit);
    setVariantKind(variant?.kind ?? 'single');
    setVariantPrice(centsToEuro(variant?.cents ?? null));
    setCategoryId(item?.category_id ?? defaultCategoryId ?? categories[0]?.id ?? '');
    setAllergens((item?.allergens ?? []) as Allergen[]);
    setDietary((item?.dietary_flags ?? []) as DietaryFlag[]);
    setVisible(item?.visible ?? true);
    setOutOfStock(item?.out_of_stock ?? false);
    setPhotoBlob(null);
    setPhotoRemoved(false);
  }, [isOpen, item, defaultCategoryId, categories]);

  const allAllergens = useMemo(() => AllergenSchema.options as Allergen[], []);
  const allDietary = useMemo(() => DietaryFlagSchema.options as DietaryFlag[], []);

  function toggleAllergen(a: Allergen): void {
    setAllergens((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));
  }
  function toggleDietary(d: DietaryFlag): void {
    setDietary((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  }

  async function save(): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed) {
      props.setToast('Nome piatto richiesto');
      return;
    }
    if (!categoryId) {
      props.setToast('Scegli una categoria');
      return;
    }
    const cents = euroToCents(price);
    if (Number.isNaN(cents)) {
      props.setToast('Prezzo non valido');
      return;
    }
    let priceUnit = item?.price_unit ?? null;
    if (props.menuType === 'takeaway') {
      try {
        if (variantKind !== 'single' && cents == null) throw new Error('Inserisci anche il prezzo del formato base.');
        priceUnit = variantKind === 'single' ? null : `takeaway:${variantKind}:${parseMenuPrice(variantPrice)}`;
      } catch (error) { props.setToast((error as Error).message); return; }
    }
    setSaving(true);
    try {
      const translations = { ...(item?.translations ?? {}) } as Record<string, Record<string, unknown>>;
      const longTrim = longDesc.trim();
      translations.it = { ...(translations.it ?? {}), long_description: longTrim };

      const base: MenuItemInput = {
        category_id: categoryId,
        name: trimmed,
        description: description.trim(),
        price_cents: cents,
        price_unit: priceUnit,
        allergens,
        dietary_flags: dietary,
        visible,
        out_of_stock: outOfStock,
        translations,
      };

      let target: MenuItem;
      if (isEdit && item) {
        target = await props.editItem(item, base as Partial<MenuItem>);
      } else {
        target = await props.addItem(base);
      }

      // Foto: upload nuovo blob, oppure rimozione.
      if (photoBlob && slug) {
        const url = await uploadItemPhoto(slug, target.id, photoBlob);
        await props.editItem(target, { photo_url: url });
      } else if (photoBlob && !slug) {
        props.setToast('Foto non caricata: manca lo slug pubblico del locale');
      } else if (photoRemoved && item?.photo_url) {
        if (slug) await removeItemPhoto(slug, target.id);
        await props.editItem(target, { photo_url: null });
      }

      props.onDone();
      props.onClose();
    } catch (err) {
      props.setToast(toUserMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function del(): Promise<void> {
    if (!item) return;
    if (!(await confirmDialog(`Eliminare "${item.name}" dal menù?`, { okText: 'Elimina', danger: true }))) return;
    setSaving(true);
    try {
      await props.removeItem(item, slug);
      props.onDone();
      props.onClose();
    } catch (err) {
      props.setToast(toUserMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <IonModal isOpen={isOpen} onDidDismiss={props.onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{isEdit ? 'Modifica piatto' : 'Nuovo piatto'}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={props.onClose}>Annulla</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonItem>
          <IonLabel position="stacked">Nome *</IonLabel>
          <IonInput value={name} onIonInput={(e) => setName(e.detail.value ?? '')} placeholder="Es. Classic Burger" />
        </IonItem>

        <IonItem>
          <IonLabel position="stacked">Categoria *</IonLabel>
          <IonSelect value={categoryId} onIonChange={(e) => setCategoryId(e.detail.value)} interface="popover">
            {categories.map((c) => (
              <IonSelectOption key={c.id} value={c.id}>
                {c.icon ? `${c.icon} ` : ''}
                {c.name}
              </IonSelectOption>
            ))}
          </IonSelect>
        </IonItem>

        <IonItem>
          <IonLabel position="stacked">{props.menuType === 'takeaway' && variantKind !== 'single' ? (variantKind === 'family' ? 'Prezzo normale (€)' : 'Prezzo panino (€)') : 'Prezzo (€)'}</IonLabel>
          <IonInput
            type="text"
            inputmode="decimal"
            value={price}
            onIonInput={(e) => setPrice(e.detail.value ?? '')}
            placeholder="Es. 10,90"
          />
        </IonItem>

        {props.menuType === 'takeaway' && <>
          <IonItem>
            <IonLabel position="stacked">Formati e prezzi</IonLabel>
            <IonSelect value={variantKind} onIonChange={(e) => setVariantKind(e.detail.value)} interface="popover">
              <IonSelectOption value="single">Prezzo unico</IonSelectOption>
              <IonSelectOption value="family">Normale e famiglia</IonSelectOption>
              <IonSelectOption value="piadina">Panino e piadina</IonSelectOption>
            </IonSelect>
          </IonItem>
          {variantKind !== 'single' && <IonItem>
            <IonLabel position="stacked">{variantKind === 'family' ? 'Prezzo famiglia (€)' : 'Prezzo piadina (€)'}</IonLabel>
            <IonInput inputmode="decimal" value={variantPrice} onIonInput={(e) => setVariantPrice(e.detail.value ?? '')} />
          </IonItem>}
        </>}

        <IonItem>
          <IonLabel position="stacked">Descrizione breve</IonLabel>
          <IonTextarea
            autoGrow
            value={description}
            onIonInput={(e) => setDescription(e.detail.value ?? '')}
            placeholder="Riga mostrata sotto il nome nel menù"
          />
        </IonItem>
        <IonItem>
          <IonLabel position="stacked">Descrizione estesa (dettaglio)</IonLabel>
          <IonTextarea
            autoGrow
            value={longDesc}
            onIonInput={(e) => setLongDesc(e.detail.value ?? '')}
            placeholder="Testo mostrato toccando il piatto (facoltativo)"
          />
        </IonItem>

        <div style={{ margin: '16px 0 4px', fontWeight: 600, fontSize: 14 }}>Foto</div>
        <PhotoPicker
          currentUrl={photoRemoved ? null : item?.photo_url ?? null}
          paperColor={paperColor}
          onStaged={(blob) => {
            setPhotoBlob(blob);
            setPhotoRemoved(false);
          }}
          onRemove={() => {
            setPhotoBlob(null);
            setPhotoRemoved(true);
          }}
          onError={props.setToast}
        />

        <div style={{ margin: '16px 0 4px', fontWeight: 600, fontSize: 14 }}>Allergeni (Reg. UE 1169/2011)</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {allAllergens.map((a) => (
            <IonChip
              key={a}
              outline={!allergens.includes(a)}
              color={allergens.includes(a) ? 'primary' : 'medium'}
              onClick={() => toggleAllergen(a)}
            >
              {ALLERGEN_LABELS_IT[a]}
            </IonChip>
          ))}
        </div>

        <div style={{ margin: '16px 0 4px', fontWeight: 600, fontSize: 14 }}>Diete</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {allDietary.map((d) => (
            <IonChip
              key={d}
              outline={!dietary.includes(d)}
              color={dietary.includes(d) ? 'success' : 'medium'}
              onClick={() => toggleDietary(d)}
            >
              {DIETARY_LABELS_IT[d]}
            </IonChip>
          ))}
        </div>

        <IonItem lines="none" style={{ marginTop: 12 }}>
          <IonLabel>Visibile nel menù</IonLabel>
          <IonToggle checked={visible} onIonChange={(e) => setVisible(e.detail.checked)} />
        </IonItem>
        <IonItem lines="none">
          <IonLabel>Esaurito (out of stock)</IonLabel>
          <IonToggle checked={outOfStock} onIonChange={(e) => setOutOfStock(e.detail.checked)} />
        </IonItem>

        <IonButton expand="block" disabled={saving} onClick={() => void save()} style={{ marginTop: 16 }}>
          {isEdit ? 'Salva modifiche' : 'Crea piatto'}
        </IonButton>
        {isEdit && (
          <IonButton expand="block" fill="clear" color="danger" disabled={saving} onClick={() => void del()}>
            Elimina piatto
          </IonButton>
        )}
      </IonContent>
    </IonModal>
  );
}
